import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OrigamiHttpResponse, OrigamiResponse, ServiceBase } from './base/service.base';
import { ErrorCode } from '../constants/error-codes';
import { RoleLevel } from '../constants/role-levels';
import { SimpleTopic, TimeZoneInfo, Topic, TopicField } from '../utils/topic-utils';
import { tap } from 'rxjs/operators';
import { ConfigServiceModel } from './config.service';
import { ErrorSeverity } from '@app/services/dialogs/components/errors-information/errors-information.component';
import { ServiceOfferingTypes } from '@app/shared/forms/dynamic-form/dynamic-fields/custom-fields/room-fields/room-form-general-room-options/room-form-general-room-options.component';

export namespace RoomsServiceOptions {
  import Site = SiteServiceModel.Site;
  import SiteSettings = SiteServiceModel.SiteSettings;

  export interface GetRoomsOptions {
    userName?: string;
    isSystem?: boolean;
    siteFieldList?: string;
    searchClause?: string;
    orderByClause?: string;
    siteIds?: string;
    startingRow?: number;
    noOfRows?: number;
    parentId?: number;
    oneLevelOnly?: boolean;
    viewmode?: number;
    programId?: number;
    companyCode?: string;
    getSiteFields?: boolean;
    getRoomTypes?: boolean;
    projectCode?: string;
    filterValue?: string;
  }

  export interface GetRoomsAndCategoriesOptions {
    all?: boolean;
    sites?: boolean;
    categories?: boolean;
    getSiteFields?: boolean;
    siteFieldsList?: string;
    parentId?: number;
    viewmode?: number;
    userId?: number;
    programId?: number;
    search?: string;
    filterValue?: string;
    searchClause?: string;
  }

  export interface UpdateSiteStatus {
    reason?: string;
    siteIds: number[];
    statusId: number;
  }

  export interface CloseRooms {
    parentId: number | null;
    siteIds: number[];
  }

  export interface LoginToSiteOptions {
    siteId: number;
    agreementInfo?: {
      AgreementId: number;
      Accepted: boolean;
    } | null;
  }

  export interface CreateSiteOptions {
    site: Site;
    existingSiteName?: string;
    siteFields: TopicField[];
    adminGroupName?: string;
    adminUserName?: string;
    adminPassword?: string;
    categoryId?: number;
    statusId?: number;
    siteSettings: SiteSettings[];
    importParams?: {
      FileToken: string;
      ImportOptions: {
        IncludeIndexFolders: boolean;
        IncludeCustomReports: boolean;
        EnableEtmf?: boolean;
        CloneInvSitesFromSiteId?: number;
        RetainInvSiteStatus?: boolean;
      };
    };
    roomLinks?: SimpleTopic[];
  }

  export interface GetRoomLinksOptions {
    siteId: number;
  }
}

export namespace SiteServiceModel {
  export interface SiteTopicField extends TopicField {
    AsOfDate: string;
    AsOfDateStr: string;
  }

  export interface Site {
    CreatedDate?: string;
    CreatedOn?: string;
    Description?: string;
    RoleLevel?: RoleLevel;
    RoleName?: string;
    SiteFields?: SiteTopicField[];
    SiteId?: number;
    SiteName?: string;
    StatusId?: number;
    TopicId?: number;
    DisableAudit?: boolean;
    CategoryId?: number;
    UserNameRoleLevel?: number;
  }

  export interface SiteSettings {
    TopicName: string;
    TopicFields: TopicField[];
  }

  export interface SitesResult {
    RoomTypes: Topic[];
    SiteDetails: Site[];
    TotalCount: number;
  }

  export interface LoginToSiteResult {
    AlfrescoSiteId: string;
    CreatedOn: string;
    DocUploadExpected: boolean;
    DocumentCount: number;
    EssentialDocumentCount: number;
    EssentialPageCount: number;
    GeneralQueryRespondersGroupId: number;
    InvestigativeSiteIds: number[];
    InvestigativeSiteIdsWhereUserIsContact: number[];
    IsCTMSIntegrationEnabled: boolean;
    IsIRBIntegrationEnabled: boolean;
    IsMobileAccessEnabled: boolean;
    CompanyCode?: string;
    IsSSUAccessible: boolean;
    IsSiteClosed: boolean;
    IsUserAnyContact: boolean;
    IsUserAuditResponseUser: boolean;
    IsUserAuditor: boolean;
    IsUserAuditorsManager: boolean;
    IsUserCRAContact: boolean;
    IsUserExternalAuditor: boolean;
    IsUserInvSiteManager: boolean;
    IsUserQueryManager: boolean;
    IsUserGeneralQueryResponder: boolean;
    IsUserSASContact: boolean;
    IsUserSSUApprover: boolean;
    IsUserSSUMember: boolean;
    IsUserWorkflowApprover: boolean;
    IsUserDocumentManager: boolean;
    IsUserDocumentOwner: boolean;
    IsUserAdministrativeWorkflowGroupMember: boolean;
    IsUserProxyDocumentOwner?: boolean;
    PageCount: number;
    ProjectCode: string;
    RoomTimeZone: TimeZoneInfo;
    UserTimeZone: TimeZoneInfo;
    SSUApproversGroupId: number;
    SSUFolderId: number;
    SSUGroupId: number;
    SiteName: string;
    SiteTypeId: number;
    SourceSiteName: string;
    SponsorId: number;
    LegalHoldDueDate: string;
    UseAsSharedWorkspace: boolean;
    RoomTypeId: number;
    IsFavorite: boolean;
    LinkedRooms?: Topic[];
    IsLmsIntegrationEnabled: boolean;
    LastVisit: string;
    SharedWorkspaceId: number;
    IsLinkedForPublishing: boolean;
    ReturnUrl?: string;
    ProgramId?: number;
    CreateSiteResult: CreateSiteResult;
    ServiceOffering: ServiceOfferingTypes;
    IsTIAutomateEnabled: boolean;
    IsMissingTIAutomateLibrary: boolean;
  }

  export interface CreateSiteResult {
    Id: number;
    SiteAdminGroupId: number;
    ErrorMessage: string;
    ErrorInfo: ErrorInfoItem[];
    SiteCreationResult: ConfigServiceModel.ExportResult;
  }

  export interface ErrorInfoItem {
    ErrorCode: number;
    ObjectName: string;
    ObjectType: string;
    Severity: ErrorSeverity;
  }

  export interface GetBarcode {
    domainId: number;
    folderId: string | null;
    investigativeSiteId: number | null;
    siteId: number;
  }
}

@Injectable()
export class SiteService extends ServiceBase {
  rooms: any;
  serviceUrl = 'SiteService.svc';

  public selectedRoomId: number = -1;

  public setSelectedRoomId(ids: number[]): void {
    this.selectedRoomId = ids?.length === 1 ? ids[0] : -1;
    if (this.selectedRoomId !== -1 && !this.roomTimeZoneInfos[this.selectedRoomId]) {
      this.saveRoomTimeZoneInfo(this.selectedRoomId);
    }
  }

  public roomTimeZoneInfos = {};

  public addRoomTimeZoneInfo(id: number, info: TimeZoneInfo): void {
    this.roomTimeZoneInfos[id] = info;
  }

  private saveRoomTimeZoneInfo(id: number): void {
    this.loginToSite({ siteId: id })
      .pipe(
        tap((res: OrigamiResponse<SiteServiceModel.LoginToSiteResult>) => {
          res.Success && this.addRoomTimeZoneInfo(id, res.Result.RoomTimeZone);
        }),
      )
      .subscribe();
  }

  updateSiteStatistics(siteId: number): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/UpdateSiteStatistics', { siteId }, null, null, true);
  }

  getSites(
    options: RoomsServiceOptions.GetRoomsOptions,
    silentErrorCodes?: ErrorCode[],
    forceRequest?,
  ): Observable<OrigamiResponse<SiteServiceModel.SitesResult>> {
    // if (options.activeOnly) {
    //   searchClause = searchClause ? `(${searchClause}) AND $$StatusId$$ == 1` : `$$StatusId$$ == 1`;
    // }
    return this.origamiPost('/GetSites', options, null, silentErrorCodes, forceRequest).pipe(
      tap((res) =>
        window.sessionStorage.setItem(
          'exportTopicsParamsBuffer',
          JSON.stringify({ requestParams: options, responseParams: res }),
        ),
      ),
    );
  }

  getSitesAndCategories(
    body: RoomsServiceOptions.GetRoomsAndCategoriesOptions,
    ...params
  ): Observable<OrigamiResponse<Topic[]>> {
    if (body.parentId == null) {
      delete body.parentId;
    }
    return this.origamiPost('/GetSitesAndCategories', body, ...params);
  }

  loginToSite(
    params: RoomsServiceOptions.LoginToSiteOptions,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<SiteServiceModel.LoginToSiteResult>> {
    return this.origamiPost('/LoginToSite', params, null, silentErrorCodes, true).pipe(
      tap((res: OrigamiResponse<SiteServiceModel.LoginToSiteResult>) => {
        res.Success && this.addRoomTimeZoneInfo(params.siteId, res.Result.RoomTimeZone);
      }),
    );
  }

  createSite(body: RoomsServiceOptions.CreateSiteOptions): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/CreateSite', body, null, null, true);
  }

  updateSiteStatus(body: RoomsServiceOptions.UpdateSiteStatus): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/UpdateSiteStatus', body, null, null, true);
  }

  modifySite(body, silentErrorCodes = []): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ModifySite', body, null, silentErrorCodes, true);
  }

  closeRooms(body: RoomsServiceOptions.CloseRooms, silentErrorCodes?: ErrorCode[]): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/CloseRooms', body, null, silentErrorCodes, true);
  }

  getBarcode(body: SiteServiceModel.GetBarcode, siteId: number, ...params) {
    return this.origamiPost('/GetBarcode', { param: body, siteId: siteId }, ...params);
  }

  getSiteDetails(body, ...params) {
    return this.origamiPost('/GetSiteDetails', body, null, null, true);
  }

  hasSitePin(options: { siteId: number }): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/HasSitePin', options);
  }

  acceptCA(
    body: RoomsServiceOptions.LoginToSiteOptions,
    options,
    silentErrorCodes = [],
    forceRequest: boolean,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/AcceptCA', body, options, silentErrorCodes, forceRequest);
  }

  getRoomLinks(body: RoomsServiceOptions.GetRoomLinksOptions, forceRequest = true) {
    return this.origamiPost('/GetRoomLinks', body, null, null, forceRequest);
  }

  resetSiteCreationStatus(body: { siteId: number }, silentErrorCodes?: ErrorCode[]) {
    return this.origamiPost('/ResetSiteCreationStatus', body, null, silentErrorCodes, true);
  }
}
